import _ from 'lodash'
import firebase from 'firebase/app'
import 'firebase/database'
import { generateRandomString } from '~js/utils'
import { getUserPublicProfile } from '../axios/services/user'
import { getEUFileUrl } from '@/utils/bucket'

// import config from './config'

// firebase.initializeApp(config) => No need to do it again, initialized in main.js already

const db = firebase.database()

const getValFromFirebase = async (path) => {
  return db.ref(path).once('value').then(snapshot => snapshot.val())
}

export const addPilotChapter = async (pilotChapters, pilotId, pilotChapterId) => {
  const oldPilotChaptersKeys = await getValFromFirebase(`/pilots/${pilotId}/naviCollection`) || []
  let firstPilotChapterChanged = _.isEmpty(oldPilotChaptersKeys)
  // push pilotChapters
  const shrinkedPilotChapters = shrinkPilotChapter(pilotChapters)
  const newPilotChapterKeys = await Promise.all(shrinkedPilotChapters.map(chapter => {
    const objectId = generateRandomString(20)
    chapter.objectId = objectId
    chapter.hidden = false
    return firebase.database().ref(`/pilotChapters/${objectId}`).set(chapter).then(() => objectId)
  }))
  let insertIndex = oldPilotChaptersKeys.length
  if (pilotChapterId) {
    const foundIndex = oldPilotChaptersKeys.findIndex(item => item === pilotChapterId)
    if (foundIndex !== -1) {
      // delete old pilotChapter
      await firebase.database().ref(`/pilotChapters/${pilotChapterId}`).remove()
      oldPilotChaptersKeys.splice(foundIndex, 1)
      if (foundIndex === 0) {
        firstPilotChapterChanged = true
      }
      insertIndex = foundIndex
    }
  }
  // update pilot
  oldPilotChaptersKeys.splice(insertIndex, 0, ...newPilotChapterKeys)
  const payload = {
    naviCollection: oldPilotChaptersKeys
  }
  if (firstPilotChapterChanged) {
    const firstPanorama = await getValFromFirebase(`/panoramas/${pilotChapters[0].panoramaId}`)
    payload.firstBuildingId = firstPanorama.Building
    payload.firstPanoramaId = firstPanorama.data.objectId
  }
  await db.ref(`/pilots/${pilotId}`).update(payload)
}

export const fetchPilot = async (pilotId) => {
  // fetch pilot
  const pilot = await getValFromFirebase(`/pilots/${pilotId}`)
  return pilot
}

export const fetchPilotChapter = async (pilotId) => {
  // fetch pilot
  const pilot = await getValFromFirebase(`/pilots/${pilotId}`)
  // fetch and expand pilotChapters
  const pilotChapters = await Promise.all(pilot.naviCollection.map(pilotChapterId => {
    const pilotChaptersRef = db.ref(`/pilotChapters/${pilotChapterId}`)
    return pilotChaptersRef.once('value').then(pilotChapter => pilotChapter.val())
  }))
  // skip pilot chapters whose building not found or unavailable
  await Promise.all(pilotChapters.map(pilotChapter => {
    const buildingRef = db.ref(`/buildings/${pilotChapter.buildingId}`)
    return buildingRef.once('value').then(snapshot => {
      const building = snapshot.val()
      if (!building || building.data.unavailable) {
        pilotChapter.hidden = true
      }
    })
  }))
  return expandPilotChapter(pilotChapters)
}

export const findPilots = async (panoramaId, userId) => {
  // find pilots
  const pilotChaptersRef = db.ref('/pilotChapters')
  const snapshots = await pilotChaptersRef.orderByChild('panoramaId').equalTo(panoramaId).once('value')
  const pilotChapters = snapshots.exportVal()
  if (!pilotChapters) {
    return { pilots: [], influencers: [] }
  }
  let pilots = []
  const pilotsIdList = []
  const influencers = []
  await Promise.all(Object.keys(pilotChapters).map(key => {
    const pilotChapter = pilotChapters[key]
    if (pilotChapter.hidden) {
      // eslint-disable-next-line array-callback-return
      return
    }
    const pilotId = pilotChapter.pilotId
    const pilotRef = db.ref(`/pilots/${pilotId}`)
    return pilotRef.once('value').then(pilotSnapshot => {
      if (!pilotSnapshot.val()) {
        return
      }
      const pilot = pilotSnapshot.val()
      if (pilot.naviCollection.includes(key) && !pilotsIdList.includes(pilotId)) {
        pilotsIdList.push(pilotId)
        delete pilot.naviCollection
        pilots.push(pilot)
      }
    })
  }))
  // check permission
  pilots = pilots.filter(pilot => {
    if (userId === pilot.ownerId) return true
    if (userId === pilot.adminId) return !pilot.hidden
    return !pilot.hidden && pilot.accept
  })
  // fetch userProfile
  // eslint-disable-next-line array-callback-return
  await Promise.all(pilots.map((pilot, index) => {
    if (!_.isNil(pilot.ownerId)) {
      return getUserPublicProfile(pilot.ownerId).then((user) => {
        if (!user) return
        const {
          name,
          // portrait,
          profileUrl
        } = user.data
        // const portraitImage = _.has(portrait, 'cutUrl') ? portrait.cutUrl : profileUrl
        const portraitImage = profileUrl
        pilots[index].ownerName = name
        pilots[index].ownerPortrait = portraitImage
        const influencerIndex = influencers.findIndex(influencer => influencer.id === pilot.ownerId)
        if (influencerIndex === -1) {
          const influencer = {
            id: pilot.ownerId,
            name,
            portraitImage,
            viewCounts: pilot.viewCounts || 0
          }
          influencers.push(influencer)
        } else {
          influencers[influencerIndex].viewCounts += (pilot.viewCounts || 0)
        }
      })
    }
  }))
  // fetch thumbnail
  // eslint-disable-next-line array-callback-return
  await Promise.all(pilots.map((pilot, index) => {
    pilot.pilotThumbnail = getEUFileUrl(pilot.pilotThumbnail)
    if (!pilot.pilotThumbnail && pilot.firstBuildingId) {
      const buildingRef = db.ref(`/buildings/${pilot.firstBuildingId}`)
      return buildingRef.once('value').then(buildingSnapshot => {
        const val = buildingSnapshot.val()
        if (!val) {
          return
        }
        pilots[index].buildingThumbnail = getEUFileUrl(val.data.thumbnail)
      })
    }
  }))
  pilots.sort((a, b) => (a.viewCounts || 0) < (b.viewCounts || 0) ? 1 : -1)
  influencers.sort((a, b) => a.viewCounts < b.viewCounts ? 1 : -1)
  // console.log('pilots', pilots)
  // console.log('influencers', influencers)

  return { pilots, influencers }
}

export const updatePilotViewCounts = async (pilotId) => {
  const pilotViewCountsRef = db.ref(`/pilots/${pilotId}/viewCounts`)
  pilotViewCountsRef.transaction(currentValue => (currentValue || 0) + 1)
}

const historyShrinkBoolMap = {
  floorplan: 'f',
  location: 'l',
  share: 's',
  panoramaList: 'p',
  markerInfo: 'm',
  popup: 'pu'
}
const historyShrinkNumMap = {
  cameraH: 'h',
  cameraV: 'v',
  cameraFov: 'fv'
}

const shrinkPilotChapter = (pilotChapters) => {
  const shrinkedPilotChapters = []
  pilotChapters.forEach((pilotChapter) => {
    const data = _.cloneDeep(pilotChapter)
    // shrink histroy
    const naviData = {
      a: pilotChapter.naviData.animation,
      h: []
    }
    pilotChapter.naviData.history.forEach((histroy, index) => {
      const shrinkedHistory = {}
      _.forEach(historyShrinkBoolMap, (abbr, full) => {
        // preserve data if it's first or it's different from the previous value
        if (index === 0 || histroy[full] !== pilotChapter.naviData.history[index - 1][full]) {
          shrinkedHistory[abbr] = histroy[full] ? 1 : 0
          if (full === 'markerInfo') {
            shrinkedHistory.id = histroy.markerId || ''
          } else if (full === 'popup') {
            shrinkedHistory.id = histroy.popupId || ''
          }
        }
      })
      _.forEach(historyShrinkNumMap, (abbr, full) => {
        // preserve data if it's first or it's different from the previous value
        if (index === 0 || histroy[full] !== pilotChapter.naviData.history[index - 1][full]) {
          shrinkedHistory[abbr] = Math.round(histroy[full] * 10) / 10
        }
      })
      naviData.h.push(shrinkedHistory)
    })
    data.naviData = JSON.stringify(naviData)
    shrinkedPilotChapters.push(data)
  })
  return shrinkedPilotChapters
}

const expandPilotChapter = (pilotChapters) => {
  const expandedPilotChapters = []
  let cumulativeDuration = 0
  pilotChapters.forEach((pilotChapter) => {
    if (pilotChapter.hidden) {
      // skip
      return
    }
    const data = _.cloneDeep(pilotChapter)
    data.naviData = JSON.parse(pilotChapter.naviData)
    // expand histroy
    const naviData = {
      animation: data.naviData.a,
      history: []
    }
    data.naviData.h.forEach((histroy) => {
      const expandedHistory = {}
      _.forEach(historyShrinkBoolMap, (abbr, full) => {
        if (!_.isNil(histroy[abbr])) {
          expandedHistory[full] = !!histroy[abbr]
          if (abbr === 'm') {
            expandedHistory.markerId = histroy.id || ''
          } else if (abbr === 'pu') {
            expandedHistory.popupId = histroy.id || ''
          }
        }
      })
      _.forEach(historyShrinkNumMap, (abbr, full) => {
        if (!_.isNil(histroy[abbr])) {
          expandedHistory[full] = histroy[abbr]
        }
      })
      naviData.history.push(expandedHistory)
    })
    data.videoUrl = getEUFileUrl(data.videoUrl)
    data.naviData = naviData
    data.cumulativeDuration = cumulativeDuration
    cumulativeDuration += data.duration
    expandedPilotChapters.push(data)
  })
  return expandedPilotChapters
}
